/*
 * Copyright (c) 2021 WIT Software. All rights reserved.
 *
 * WIT Software Confidential and Proprietary information. It is strictly forbidden for 3rd parties to modify, decompile,
 * disassemble, defeat, disable or circumvent any protection mechanism; to sell, license, lease, rent, redistribute or
 * make accessible to any third party, whether for profit or without charge.
 *
 */

import React, { useState } from 'react';
// Components
import logo from 'assets/images/vf-logo.svg';
import { Link } from 'react-router-dom';
import { useHistory, useLocation } from 'react-router';
import { PrivateRoutePath, PublicRoutePath, StartUpRoutePath } from 'routes/paths';
import { useDispatch, useSelector } from 'react-redux';
import { Selectors as StartUpSelectors } from 'store/ducks/startUp';
import { Ducks } from '@wit/vtv-x-foundation-lib';
import translate from 'services/i18n/translate';
import AppConfig from 'services/AppConfig';
import RouterUtils from 'services/utils/RouterUtils';
import MainSection from 'types/MainSection';
import { EnvLinkKeys } from 'types/Env';

const Footer = React.memo(() => {
  const year = new Date().getFullYear();
  const routerHistory = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();

  const isStartUpSuccess = useSelector(StartUpSelectors.isStartUpSuccess);
  const isAfterInitializingCredentials = useSelector(StartUpSelectors.isAfterInitializingCredentials);
  const isAuthenticated = useSelector(Ducks.Credentials.Selectors.isAuthenticated);

  // Consent and Notice GDPR Links
  const gdprData = useSelector(Ducks.Faqs.Selectors.getGdprData);
  const { notice, consent } = gdprData?.documents || {};

  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [isAccountOpen, setIsAccountOpen] = useState(false);
  const [isAboutOpen, setIsAboutOpen] = useState(false);

  const handleSignOut = () => {
    dispatch(Ducks.Credentials.Creators.logout());
  };

  const termsAndConditions = AppConfig.env?.ABOUT_LINKS?.find(
    (e) => e.id.toLowerCase() === EnvLinkKeys.TERMS_AND_CONDITIONS.toLowerCase()
  );

  const termsAndConditionsLink = () => {
    let termsUrl: string = MainSection.WATCH_TV;
    if (location.pathname) {
      // Remove the leading '/' of the pathname
      const locationUrl = location.pathname.substring(1);
      if (Object.values(MainSection).includes(locationUrl as MainSection)) {
        termsUrl = locationUrl;
      }
    }

    routerHistory.push(
      RouterUtils.getRouteWithDynamicValues(PublicRoutePath.ABOUT_ELEMENT, [
        termsUrl,
        EnvLinkKeys.TERMS_AND_CONDITIONS.toLowerCase(),
      ])
    );
  };

  return (
    <div className="component footer-wrapper">
      <div className="component-inner footer">
        <div className="footer-cols">
          {isAuthenticated && isStartUpSuccess && (
            <div id="Footer_linkSection" className={`footer-link-section ${isSettingsOpen ? 'expanded' : ''}`}>
              <a
                id="Footer_expandableLink"
                className="footer-expandable-link"
                onClick={() => setIsSettingsOpen((prevState) => !prevState)}
              >
                <h3>{translate['footer.settings.title']}</h3>
              </a>
              <ul id="Footer_collapsedLink" className="footer-links-collapsed">
                <Link id="Footer_button___parentalControl" to={PrivateRoutePath.SETTINGS_PARENTAL}>
                  <li>
                    <span className="text-link">{translate['footer.settings.parentalControl']}</span>
                  </li>
                </Link>
              </ul>
            </div>
          )}
          {isAfterInitializingCredentials ? (
            <div className={`footer-link-section ${isAccountOpen ? 'expanded' : ''}`}>
              <a className="footer-expandable-link" onClick={() => setIsAccountOpen((prevState) => !prevState)}>
                <h3>{translate['footer.account.title']}</h3>
              </a>
              <ul id="Footer_buttonList" className="footer-links-collapsed">
                {isAuthenticated ? (
                  <a id="Footer_button___signOut" onClick={handleSignOut}>
                    <li>{translate['menu.profile.signOut']}</li>
                  </a>
                ) : (
                  <Link id="Footer_button___signIn" to={StartUpRoutePath.SIGNIN}>
                    <li>{translate['menu.profile.signIn']}</li>
                  </Link>
                )}
              </ul>
            </div>
          ) : (
            <></>
          )}
          <div className={`footer-link-section ${isAboutOpen ? 'expanded' : ''}`}>
            <a className="footer-expandable-link" onClick={() => setIsAboutOpen((prevState) => !prevState)}>
              <h3>{translate['footer.about.title']}</h3>
            </a>
            <ul id="Footer_privacy" className="footer-links-collapsed">
              {notice && (
                <Link id="Footer_button___privacyNotice" to={PublicRoutePath.PRIVACY_NOTICE}>
                  <li>
                    <span className="text-link">{translate['footer.about.privacyNotice']}</span>
                  </li>
                </Link>
              )}
              {consent && (
                <Link id="Footer_button___privacyConsent" to={PublicRoutePath.PRIVACY_CONSENT}>
                  <li>
                    <span className="text-link">{translate['footer.about.privacyConsent']}</span>
                  </li>
                </Link>
              )}
              {termsAndConditions && (
                <a id="Footer_button___termsAndConditions" onClick={termsAndConditionsLink}>
                  <li>
                    <span className="text-link">{translate['footer.about.termsAndConditions']}</span>
                  </li>
                </a>
              )}
            </ul>
          </div>
        </div>
        <div className="footer-company-info" id="Footer_companyInfo">
          <div className="vodafone-logo">
            <img id="Footer_companyLogo" className="vf-logo-full" src={logo} alt="vodafone logo" />
          </div>
          <p id="Footer_companyInfo___0" className="company-info">
            {translate.formatString(translate['company.info.line1'], { year }).toString()}
          </p>
          <p id="Footer_companyInfo___1" className="company-info">
            {translate['company.info.line2']}
          </p>
          <p id="Footer_companyInfo___2" className="company-info">
            {translate['company.info.line3']}
          </p>
        </div>
      </div>
    </div>
  );
});

export default Footer;
